import Head from 'next/head';
import { useMemo } from 'react';
import { usePDPData, usePDPState } from '~/features/product-details/context';
import { Breadcrumb } from '~/lib';
import { getSalesforceProductImages } from '~/lib/salesforce/utilities/product-images';
import { buildPdpSchemaGraph, type ReviewSource } from '~/services/schemas/product/salesforce';
import { getRuntimeConfig } from '~/shared/utils/config';
import { cloudflareLoader } from '~/shared/utils/loaders/cloudflareLoader';

const IMAGE_WIDTH = 720;

type Props = {
    breadcrumb?: Breadcrumb;
    // Fetched server side so the Review markup is in the delivered document.
    topReviews?: ReviewSource[] | null;
};

export const ProductSchema = ({ breadcrumb, topReviews }: Props) => {
    const { config } = getRuntimeConfig();
    const { product } = usePDPData();
    const { promotionCountdown, isPromotionDiscountHeldBack } = usePDPState();
    const countdownRegularPrice = isPromotionDiscountHeldBack ? promotionCountdown?.price : null;

    const siteUrl = (config.NEXT_PUBLIC_URL || '').replace(/\/$/, '');

    const innerHTML = useMemo<{ __html: string } | null>(() => {
        if (!product) return null;

        const imageUrls = (getSalesforceProductImages(product) ?? [])
            .filter((image) => !!image?.link)
            .map((image) => cloudflareLoader({ src: image.link, width: IMAGE_WIDTH }));

        const schema = buildPdpSchemaGraph({
            product,
            breadcrumb,
            siteUrl,
            imageUrls,
            reviews: topReviews ?? [],
            countdownRegularPrice,
        });

        return { __html: JSON.stringify(schema).replace(/</g, '\\u003c') };
    }, [product, breadcrumb, siteUrl, topReviews, countdownRegularPrice]);

    if (!innerHTML) return null;

    return (
        <Head>
            <script
                key="pdp-schema"
                type="application/ld+json"
                dangerouslySetInnerHTML={innerHTML}
            />
        </Head>
    );
};
